import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatRelative } from '../lib/format';
import type { BlackbaudAddinLink, BlackbaudIntegration } from '../lib/types';
import { CopyField, useIntegrationMutations } from './integration-shell';
import { useToast } from './toast';
import { Alert, Badge, Button, ConfirmDialog, Field, Input, Spinner, Switch, Table, Td, Th } from './ui';

/** Blackbaud's Page Host Button: a button on a Blackbaud page that opens the Edvora page with that page's context. */
function pageButtonUrl(applicationId: string, pageName: string) {
  const title = encodeURIComponent('Edvora Campus Asset');
  return `https://app.blackbaud.com/addin-page-host-button?appId=${encodeURIComponent(applicationId)}&pageName=${encodeURIComponent(pageName)}&title=${title}&svcid=edu`;
}

/** The Blackbaud SKY add-ins: a tile on contact cards, and a page where technicians find people and devices. */
export function BlackbaudAddin({ integration }: { integration: BlackbaudIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { update } = useIntegrationMutations('BLACKBAUD');
  const bb = integration.blackbaud;
  const [addinEnabled, setAddinEnabled] = useState(integration.settings.addinEnabled);
  const [addinPageEnabled, setAddinPageEnabled] = useState(integration.settings.addinPageEnabled);
  const [addinMatchByEmail, setAddinMatchByEmail] = useState(integration.settings.addinMatchByEmail);

  const problem =
    integration.mode === 'DEMO'
      ? 'The add-ins need a live Blackbaud connection. Switch the data source to Blackbaud on the Connection tab.'
      : !bb.connected
        ? 'Connect Blackbaud first. The add-ins find your school by the Blackbaud environment you connect.'
        : !bb.addin.environmentId
          ? 'Reconnect Blackbaud so Edvora learns which Blackbaud environment is yours. The add-ins find your school by it.'
          : null;

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Edvora inside Blackbaud</h3>
          <p className="text-sm text-slate-500">
            Two SKY add-ins bring Edvora into Blackbaud Education Management. A tile on each person’s contact card lists the devices they have
            checked out and their Chromebook sign-ins from the last two weeks. The Edvora page is a full Blackbaud page where technicians and
            administrators search people and devices, see what’s checked out and overdue, and check devices out and in. Both are built with
            Blackbaud’s SKY UX, so they look like the rest of Blackbaud.
          </p>
        </div>
        {problem && <Alert tone="amber">{problem}</Alert>}
      </section>

      <section className="max-w-xl space-y-4">
        <Switch
          label="Show the Edvora tile on contact cards"
          description="Only Blackbaud users matched to an Edvora account that can see devices get any data from it. Technicians and administrators can check devices out and in from it."
          checked={addinEnabled}
          disabled={!isAdmin}
          onChange={setAddinEnabled}
        />
        <Switch
          label="Turn on the Edvora page"
          description="Only Edvora technicians and administrators can use it. Anyone else who opens it is told it isn’t for them."
          checked={addinPageEnabled}
          disabled={!isAdmin}
          onChange={setAddinPageEnabled}
        />
        <Switch
          label="Match Blackbaud users to Edvora accounts by email"
          description="Someone whose Blackbaud email is an Edvora account’s at this school uses the add-ins as that account. Anyone else links their account by signing in to Edvora once, from the tile or the page."
          checked={addinMatchByEmail}
          disabled={!isAdmin}
          onChange={setAddinMatchByEmail}
        />
        {isAdmin && (
          <Button variant="primary" loading={update.isPending} onClick={() => update.mutate({ settings: { addinEnabled, addinPageEnabled, addinMatchByEmail } })}>
            Save add-in settings
          </Button>
        )}
      </section>

      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Register the add-ins in Blackbaud</h3>
          <p className="text-sm text-slate-500">
            {bb.appSource === 'platform'
              ? 'You connected with Edvora’s SKY application, and Edvora registers both add-ins for it once for every school. There’s nothing to do in the Blackbaud developer portal.'
              : 'Whoever manages your school’s SKY application registers each add-in once, in the Blackbaud developer portal: open the application, choose Add-ins, and add one for each extension point below with its URL. Name the page add-in exactly as shown, because Blackbaud finds the page by its name.'}
          </p>
        </div>
        {bb.addin.url.startsWith('http://') && (
          <Alert tone="amber">Blackbaud only loads add-ins over HTTPS. Register them once Edvora runs at an https:// address.</Alert>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <CopyField label="Contact card tile URL" value={bb.addin.url} hint="For the Contact card tile extension point." />
          </div>
          <div className="sm:col-span-2">
            <CopyField label="Page URL" value={bb.addin.pageUrl} hint="For the Page extension point." />
          </div>
          <CopyField label="Page add-in name" value={bb.addin.pageName} hint="The name to register the page under." />
          {bb.addin.applicationId ? (
            <CopyField label="SKY application ID" value={bb.addin.applicationId} hint="The application the add-ins are registered under." />
          ) : (
            <Field label="Extension points">
              <Input readOnly value="Contact card tile, Page" />
            </Field>
          )}
        </div>
      </section>

      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Open the Edvora page</h3>
          <p className="text-sm text-slate-500">
            Blackbaud shows the page at this address, below Education Management’s navigation. Share it with your technicians or add it to a
            link list in Blackbaud. The tile on a contact card also takes technicians to that person on the page.
          </p>
        </div>
        {bb.addin.pageHostUrl ? (
          <div className="grid gap-4">
            <CopyField label="Page address in Blackbaud" value={bb.addin.pageHostUrl} />
            {bb.addin.applicationId && (
              <CopyField
                label="Button add-in URL (optional)"
                value={pageButtonUrl(bb.addin.applicationId, bb.addin.pageName)}
                hint="Register it for a Blackbaud page’s button extension point to put an “Edvora Campus Asset” button there. The button opens the Edvora page with that page’s context."
              />
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Connect Blackbaud to see where the page opens.</p>
        )}
      </section>

      {isAdmin && <AddinLinks />}
    </div>
  );
}

const METHOD_LABEL: Record<BlackbaudAddinLink['method'], string> = { EMAIL: 'Email match', SIGN_IN: 'Signed in to Edvora' };

function AddinLinks() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<BlackbaudAddinLink | null>(null);
  const links = useQuery({
    queryKey: ['blackbaud-addin-links'],
    queryFn: () => api.get<BlackbaudAddinLink[]>('/integrations/blackbaud/addin-links'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/integrations/blackbaud/addin-links/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['blackbaud-addin-links'] });
      setRemoving(null);
      toast.success('Link removed');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Linked Blackbaud users</h3>
        <p className="text-sm text-slate-500">Who uses the add-ins as which Edvora account. What they can do comes from their Edvora role.</p>
      </div>
      {links.isLoading ? (
        <Spinner />
      ) : links.error ? (
        <Alert tone="red">{errorMessage(links.error)}</Alert>
      ) : links.data!.length === 0 ? (
        <p className="text-sm text-slate-500">Nobody has used the add-ins yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <Table>
            <thead>
              <tr>
                <Th>Blackbaud user</Th>
                <Th>Edvora account</Th>
                <Th>Linked by</Th>
                <Th>Last used</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {links.data!.map((link) => (
                <tr key={link.id}>
                  <Td>
                    <p className="font-medium text-slate-900">{link.blackbaudName ?? 'Blackbaud user'}</p>
                    {link.blackbaudEmail && <p className="text-xs text-slate-500">{link.blackbaudEmail}</p>}
                  </Td>
                  <Td>
                    <p className="text-slate-900">{link.user.name}</p>
                    <p className="text-xs text-slate-500">{link.user.email}</p>
                  </Td>
                  <Td>
                    <Badge tone={link.method === 'EMAIL' ? 'gray' : 'blue'}>{METHOD_LABEL[link.method]}</Badge>
                    <p className="mt-1 text-xs text-slate-500">{formatDate(link.createdAt)}</p>
                  </Td>
                  <Td className="whitespace-nowrap">{formatRelative(link.lastUsedAt)}</Td>
                  <Td className="text-right">
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setRemoving(link)} aria-label={`Remove the link for ${link.blackbaudEmail ?? link.user.name}`}>
                      Remove
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
        loading={remove.isPending}
        title="Remove this link?"
        confirmLabel="Remove link"
        description={
          removing && (
            <>
              {removing.blackbaudEmail ?? removing.blackbaudName ?? 'This Blackbaud user'} stops using the add-ins as {removing.user.name}.
              {removing.method === 'EMAIL' &&
                ' While email matching is on and their Blackbaud email is still that account’s, they’re matched again the next time they open the tile or the page.'}
            </>
          )
        }
      />
    </section>
  );
}
